import { Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import HeroCard from './assets/hero-card.svg';
import HeroDocument from './assets/hero-document.svg';
import HeroFolder from './assets/hero-folder.svg';

type HeroVariant = 'page' | 'popup';

// The composition is one image (role="img" on the wrapper), so the words are
// styled spans rather than heading landmarks.
const HeroWord = ({ children }: { children: ReactNode }) => (
  <Heading
    className="mb-0 whitespace-nowrap font-studio text-[37.932px] font-normal leading-[1.1] tracking-[-1.138px] text-white"
    size="3xl"
    tag="span"
  >
    {children}
  </Heading>
);

const HeroTile = ({
  variant,
  children,
}: {
  variant: HeroVariant;
  children: ReactNode;
}) => (
  <span
    className={clsx(
      'flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-[12.2px] bg-white/50 p-[3.35px] box-border',
      { 'max-sm:bg-white/30': variant === 'popup' },
    )}
  >
    {children}
  </span>
);

export function ReferralsHero({ variant = 'page' }: { variant?: HeroVariant }) {
  const { t } = useTranslation();
  const isPopup = variant === 'popup';

  return (
    <div
      className={clsx(
        'relative flex w-full shrink-0 items-center justify-center overflow-hidden bg-[#101010] @container sm:h-[226px]',
        {
          'h-[169px]': isPopup,
          'h-[204px] rounded': !isPopup,
        },
      )}
      data-testid="referrals-hero"
    >
      {/* Mobile keeps the desktop composition, scaled to each Figma frame:
          311/378 on the settings page, 254/378 in the sidebar popup. The
          offsets are Figma's placement, which sits slightly off centre (the
          translate is unscaled, so mobile carries its own value). Banners
          narrower than @xs can't fit the page's scale, so they step down
          rather than clip the composition at both edges. */}
      <div
        aria-label={`${t(($) => $.page_settings_referrals.hero.turn)} ${t(($) => $.page_settings_referrals.hero.referrals)} ${t(($) => $.page_settings_referrals.hero.into)} ${t(($) => $.page_settings_referrals.hero.more_builds)}`}
        className={clsx('flex flex-col items-center', {
          'translate-y-[9px] max-sm:translate-y-[6px] max-sm:scale-[0.672]':
            isPopup,
          'max-sm:scale-75 max-sm:@xs:scale-[0.823] sm:-translate-x-1.5 sm:-translate-y-0.5':
            !isPopup,
        })}
        role="img"
      >
        <div className="flex items-center gap-2">
          <HeroWord>{t(($) => $.page_settings_referrals.hero.turn)}</HeroWord>
          <HeroTile variant={variant}>
            {/* The Figma slot is smaller than the asset and bleeds it up-left. */}
            <span className="relative h-[29.667px] w-[21.034px]">
              <img
                alt=""
                className="absolute bottom-0 right-0 block h-[33.905px] w-[26.63px] max-w-none"
                src={HeroDocument}
              />
            </span>
          </HeroTile>
          <HeroWord>
            {t(($) => $.page_settings_referrals.hero.referrals)}
          </HeroWord>
        </div>
        <div className="flex items-center gap-2">
          <HeroWord>{t(($) => $.page_settings_referrals.hero.into)}</HeroWord>
          <HeroTile variant={variant}>
            <img
              alt=""
              className="block h-[26px] w-[40.916px] max-w-none"
              src={HeroCard}
            />
          </HeroTile>
          <HeroWord>
            {t(($) => $.page_settings_referrals.hero.more_builds)}
          </HeroWord>
          <HeroTile variant={variant}>
            <img
              alt=""
              className="block h-[23.884px] w-[33.609px] max-w-none -scale-x-100"
              src={HeroFolder}
            />
          </HeroTile>
        </div>
      </div>
    </div>
  );
}
